import { DeleteOutlined, EditOutlined, MenuOutlined } from '@ant-design/icons';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Badge, Form } from 'antd';
import type { FormListFieldData } from 'antd/es/form/FormList';
import React from 'react';
import { Link } from 'react-router-dom';

import { SListItem } from 'app/components/StaycationUI/List';
import type { ExperienceType } from 'app/components/commons/Package/General/Types';
import { PictureComponent } from 'app/components/commons/PictureComponent/PictureComponent';
import { Option, Select } from 'app/components/fields/Select';

import './ExperienceItem.scss';

const RULES = [{ required: true, message: 'Required' }];

const INCLUDE_OPTIONS = ['Included', 'Add-on'];

type ExperienceItemTitleProps = {
  experience: ExperienceType;
};

const ExperienceItemTitle = ({ experience }: ExperienceItemTitleProps) => {
  return (
    <div className="experienceItemTitle">
      {experience.title}
      {experience.published ? (
        <Badge status="success" text="Published" />
      ) : (
        <Badge status="warning" text="Unpublished" />
      )}
    </div>
  );
};

export const ExperienceItem = ({
  field,
  experience,
  mode,
  remove,
  dndId,
  disabled,
}: Props) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: dndId ?? field?.key ?? '', disabled: !!disabled });

  if (!field || !experience) {
    return null;
  }

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const avatar =
    (experience.included === 'Included'
      ? experience.pictures?.[0]
      : experience.emoji) || experience.avatar;

  const renderAvatar = () => (
    <div
      className={`experience-item__avatar ${
        avatar?.name.includes('emoji') ? 'experience-item__avatar-emoji' : ''
      }`}
    >
      {avatar && <PictureComponent {...avatar} width={56} />}
    </div>
  );

  const renderIncluded = () => (
    <Form.Item name={[field.name, 'included']} rules={RULES} noStyle>
      {mode === 'edit' ? (
        <Select
          optionFilterProp="children"
          placeholder="Select"
          popupClassName="dropdown-experiences"
        >
          {INCLUDE_OPTIONS.map((value) => (
            <Option key={value} value={value}>
              {value}
            </Option>
          ))}
        </Select>
      ) : (
        <div className="view-text">{experience.included}</div>
      )}
    </Form.Item>
  );

  const renderEdit = () => (
    <div className="edit-link">
      <Link to={`/activities/legacy/${experience.id}`} target="_blank">
        <EditOutlined />
      </Link>
    </div>
  );

  const renderDelete = () => <DeleteOutlined onClick={remove} />;

  const renderDragHandler = () => (
    <MenuOutlined
      key="handle"
      className="experience-item__drag-handle"
      style={{ cursor: 'grab' }}
      {...attributes}
      {...listeners}
    />
  );

  const renderActions = () => {
    if (mode === 'edit') {
      return [
        renderIncluded(),
        renderEdit(),
        renderDelete(),
        renderDragHandler(),
      ];
    }

    return [renderIncluded()];
  };

  return (
    <SListItem
      ref={setNodeRef}
      style={style}
      title={<ExperienceItemTitle experience={experience} />}
      description={experience.description}
      avatar={renderAvatar()}
      actions={renderActions()}
      className="experience-item"
    />
  );
};

type Props = {
  field?: FormListFieldData;
  experience?: ExperienceType;
  mode: string;
  remove: () => void;
  dndId?: number;
  disabled?: boolean;
};
